"use client";

import type { TaskNotification } from "@/lib/task-notifications";

export type VisibleTaskNotification = {
  id: number;
  notification: TaskNotification;
};

type TaskNotificationToastStore = {
  nextId: number;
  notifications: VisibleTaskNotification[];
  listeners: Set<() => void>;
  dismissTimers: Map<number, number>;
};

type TaskNotificationWindow = Window & {
  __FINDIN_TASK_NOTIFICATION_TOAST_STORE__?: TaskNotificationToastStore;
};

const EMPTY_NOTIFICATIONS: VisibleTaskNotification[] = [];
const MAX_VISIBLE_NOTIFICATIONS = 3;
const TASK_NOTIFICATION_DURATION_MS = 30000;

function getStore() {
  if (typeof window === "undefined") return null;

  const taskNotificationWindow = window as TaskNotificationWindow;
  taskNotificationWindow.__FINDIN_TASK_NOTIFICATION_TOAST_STORE__ ??= {
    nextId: 0,
    notifications: [],
    listeners: new Set(),
    dismissTimers: new Map(),
  };
  return taskNotificationWindow.__FINDIN_TASK_NOTIFICATION_TOAST_STORE__;
}

function notifyStoreListeners(store: TaskNotificationToastStore) {
  store.listeners.forEach((listener) => listener());
}

export function dismissTaskNotificationToast(toastId: number) {
  const store = getStore();
  if (!store) return;

  const timer = store.dismissTimers.get(toastId);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    store.dismissTimers.delete(toastId);
  }
  const nextNotifications = store.notifications.filter(({ id }) => id !== toastId);
  if (nextNotifications.length === store.notifications.length) return;

  store.notifications = nextNotifications;
  notifyStoreListeners(store);
}

export function enqueueTaskNotificationToast(notification: TaskNotification) {
  const store = getStore();
  if (!store) return false;

  const toastId = ++store.nextId;
  const expiredNotifications = store.notifications.slice(0, -MAX_VISIBLE_NOTIFICATIONS + 1);
  expiredNotifications.forEach(({ id }) => {
    const timer = store.dismissTimers.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    store.dismissTimers.delete(id);
  });
  store.notifications = [
    ...store.notifications.slice(-MAX_VISIBLE_NOTIFICATIONS + 1),
    { id: toastId, notification },
  ];
  store.dismissTimers.set(toastId, window.setTimeout(() => {
    dismissTaskNotificationToast(toastId);
  }, TASK_NOTIFICATION_DURATION_MS));
  notifyStoreListeners(store);
  return true;
}

export function subscribeTaskNotificationToasts(listener: () => void) {
  const store = getStore();
  if (!store) return () => {};

  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
}

export function getTaskNotificationToastSnapshot() {
  return getStore()?.notifications ?? EMPTY_NOTIFICATIONS;
}

export function getTaskNotificationToastServerSnapshot() {
  return EMPTY_NOTIFICATIONS;
}
